import { inject, Injectable } from '@angular/core';
import { catchError, concat, concatMap, defer, map, Observable, of } from 'rxjs';
import { BackendService, HttpOptions } from '@ipg/ngx-frontend-toolset';
import { StatusResult } from '../models/common/utils';
import { MediaContext, mediaBasePath } from '../models/media-context.model';
import { MediaCreateResponse, MediaResponse } from '../models/media.model';

export type MediaReplaceRefResult = StatusResult<'detach' | 'attach' | 'noop'>;

const UNIT_CTX: MediaContext = { scope: 'unit' };

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  private readonly _backend = inject(BackendService);

  create(data: unknown, options?: HttpOptions, ctx: MediaContext = UNIT_CTX) {
    return this._backend.post<MediaCreateResponse>(mediaBasePath(ctx), data, options);
  }

  update(id: string, data: unknown, options?: HttpOptions, ctx: MediaContext = UNIT_CTX): Observable<unknown> {
    return this._backend.put(`${mediaBasePath(ctx)}/${id}`, data, options);
  }

  delete(id: string, options?: HttpOptions, ctx: MediaContext = UNIT_CTX): Observable<unknown> {
    return this._backend.delete(`${mediaBasePath(ctx)}/${id}`, options);
  }

  deleteGroup(groupId: string, params?: { skip_referenced?: boolean }, options?: HttpOptions): Observable<unknown> {
    return this._backend.delete(`/media/groups/${groupId}`, { ...options, params: { ...options?.params, ...params } });
  }

  getAll(options?: HttpOptions, ctx: MediaContext = UNIT_CTX): Observable<MediaResponse[]> {
    return this._backend.get(mediaBasePath(ctx), options);
  }

  import(sourceMediaId: string, ctx: MediaContext, groupId?: string | null): Observable<MediaResponse> {
    return this._backend.post<MediaResponse>(`${mediaBasePath(ctx)}/import`, {
      sourceMediaId,
      ...(groupId ? { groupId } : {}),
    });
  }

  attachRef(
    fileId: string,
    refName: string,
    refId: string,
    options?: HttpOptions
  ): Observable<unknown> {
    return this._backend.post(
      `/media/references`,
      {
        fileId,
        refName,
        refId,
      },
      options
    );
  }

  detachRef(id: string, refId: string, options?: HttpOptions): Observable<unknown> {
    return this._backend.delete(`/media/references/${id}/${refId}`, options);
  }

  replaceMediaRef(
    oldFileId: string | null | undefined,
    newFileId: string,
    refName: string,
    refId: string
  ): Observable<MediaReplaceRefResult> {
    const hasOld = !!(oldFileId && newFileId.trim());

    const attach$ = defer(() => this.attachRef(newFileId, refName, refId)).pipe(
      map((): MediaReplaceRefResult => ({ data: 'attach', ok: true })),
      catchError((error) =>
        of<MediaReplaceRefResult>({ data: 'attach', ok: false, error })
      )
    );

    // No previous media selected → just attach
    if (!hasOld) {
      return attach$;
    }

    // Same ID as before → nothing to do
    if (oldFileId === newFileId) {
      return of<MediaReplaceRefResult>({ data: 'noop', ok: true });
    }

    const detach$ = this.detachRef(oldFileId!, refId).pipe(
      map((): MediaReplaceRefResult => ({ data: 'detach', ok: true })),
      catchError((error) =>
        of<MediaReplaceRefResult>({ data: 'detach', ok: false, error })
      )
    );

    // Always attach, even if detach fails
    return detach$.pipe(concatMap((detachResult) => concat(of(detachResult), attach$)));
  }

  moveTo(
    { mediaIds, groupId }: { mediaIds: string[]; groupId: string | null },
    ctx: MediaContext = UNIT_CTX,
  ) {
    return this._backend.put(`${mediaBasePath(ctx)}/move`, { mediaIds, groupId });
  }
}
